import { ReactElement, useState } from 'react';
import { Device, DeviceCommand, DeviceConfigGeoAlert } from '@/api';
import {
    UIButton,
    UISection,
    UISectionContent,
    UISectionHeader,
    UIText,
    UITooltip,
    useUIConfirmationModal
} from '@/ui';
import {
    useCancelDeviceGeoAlertAction,
    useUpdateDeviceGeoAlertAction
} from '../actions';
import { useDevicesTranslation } from '../hooks';
import { useDeviceAlertLicenses } from '../queries';
import { GeofenceAlertModal } from './geofence-alert-modal';
import { GeofenceViewModal } from './geofence-view-modal';
import { PendingBadge } from './pending-badge';
import { DiffCell, StatusCell } from './pending-cells';
import styles from './device-geofence-section.module.scss';

export type DeviceGeofenceSectionProps = {
    device: Device;
    /** Whether the current user may edit this device. */
    canEdit: boolean;
};

/**
 * Dedicated "Geofence" section of the device page: a description followed by a
 * vertical list of the device's four geofence slots. Mirrors the team
 * escalation contacts section layout (section title as header, description and
 * list in the body).
 */
export function DeviceGeofenceSection(
    props: DeviceGeofenceSectionProps
): ReactElement {
    const { device, canEdit } = props;
    const { t } = useDevicesTranslation();
    const licenses = useDeviceAlertLicenses({
        companyId: device.companyId ?? '',
        deviceId: device.id
    });

    // Geofence is licensed per device (one licence per device using any slot).
    // `canEnableNew` is true when this device is entitled — i.e. there is room
    // within the quota once the other devices' usage is counted. When it isn't
    // entitled, editing/creating geofences is blocked (the user can still delete
    // existing ones to wind down). Show "(Upgrade required)" when the company
    // owns no addon, otherwise "(Limit reached)". Until usage has loaded we
    // don't know the entitlement, so editing is disabled but no error is shown.
    const blocked = !licenses.loading && !licenses.geo.canEnableNew;

    return (
        <UISection>
            <UISectionHeader title={t('shared.devices.geofence_label')} />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <UIText type="default2">
                        {t('components.device_geofence_section.description')}
                        {blocked && (
                            <UIText medium type="error">
                                {licenses.geo.limit === 0
                                    ? t('shared.devices.upgrade_required')
                                    : t('shared.devices.limit_reached')}
                            </UIText>
                        )}
                    </UIText>

                    <DeviceGeofenceList
                        device={device}
                        canEdit={canEdit}
                        editBlocked={blocked || licenses.loading}
                    />
                </div>
            </UISectionContent>
        </UISection>
    );
}

type DeviceGeofenceListProps = {
    device: Device;
    canEdit: boolean;
    /** When true, enabling a new geofence is blocked (no spare licence). */
    editBlocked?: boolean;
};

// A device has four geofence slots (indices 0-3). The config record is sparse,
// so we always render all four and treat a missing slot as disabled/empty.
const GEOFENCE_SLOT_COUNT = 4;

const EMPTY = '—';

/**
 * Table of the device's four geofence slots, each showing its current reported
 * state plus view/edit/delete buttons. View opens a read-only map, edit opens
 * the geofence editor, and delete (after confirmation) disables the slot and
 * clears its shape.
 */
function DeviceGeofenceList(props: DeviceGeofenceListProps): ReactElement {
    const { device, canEdit, editBlocked = false } = props;
    const { t } = useDevicesTranslation();
    const { updateDeviceGeoAlert } = useUpdateDeviceGeoAlertAction({
        deviceId: device.id
    });
    const { cancelDeviceGeoAlert } = useCancelDeviceGeoAlertAction({
        deviceId: device.id
    });
    const confirmationModal = useUIConfirmationModal();

    const [editingSlot, setEditingSlot] = useState<number | null>(null);
    const [viewing, setViewing] = useState<{
        slot: number;
        current: DeviceConfigGeoAlert | undefined;
        pending: DeviceConfigGeoAlert | undefined;
    } | null>(null);

    function confirmDelete(slot: number) {
        confirmationModal.open({
            title: t('components.device_geofence_section.delete_confirm_title'),
            confirmText: t('components.device_geofence_section.delete_confirm'),
            confirmVariant: 'danger',
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.setLoading(true);
                updateDeviceGeoAlert({
                    slot,
                    data: { enabled: false, shape: null, points: null }
                })
                    .catch(() => {}) // Error handled in the action
                    .finally(() => confirmationModal.close());
            },
            onCancel: () => confirmationModal.close()
        });
    }

    function confirmCancel(run: () => Promise<unknown>) {
        confirmationModal.open({
            title: t('shared.devices.cancel_pending_confirm_title'),
            confirmText: t('shared.devices.cancel_pending_confirm'),
            confirmVariant: 'danger',
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.setLoading(true);
                run()
                    .catch(() => {}) // Error handled in the action
                    .finally(() => confirmationModal.close());
            },
            onCancel: () => confirmationModal.close()
        });
    }

    return (
        <>
            <table className={styles.geofenceTable}>
                <thead>
                    <tr>
                        <th>
                            <HeaderText
                                text={t('shared.devices.column_index')}
                            />
                        </th>
                        <th>
                            <HeaderText text={t('shared.labels.status')} />
                        </th>
                        <th>
                            <HeaderText
                                text={t('shared.devices.shape_label')}
                            />
                        </th>
                        <th>
                            <HeaderText
                                text={t(
                                    'components.device_geofence_section.column_direction'
                                )}
                            />
                        </th>
                        <th>
                            <HeaderText
                                text={t(
                                    'components.device_geofence_section.column_size'
                                )}
                            />
                        </th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: GEOFENCE_SLOT_COUNT }, (_, index) => {
                        const slotConfig =
                            device.config?.geoAlert?.[String(index)];
                        const pendingCommand = device.commands?.find(
                            (c) =>
                                c.type === 'set_geo_alert' &&
                                c.status === 'pending' &&
                                c.data.geoAlertIndex === index
                        );
                        const pendingConfig = pendingCommand
                            ? pendingToGeoAlert(pendingCommand.data)
                            : undefined;

                        return (
                            <GeofenceSlotRow
                                key={index}
                                index={index}
                                slot={slotConfig}
                                pendingCommand={pendingCommand}
                                pendingConfig={pendingConfig}
                                canEdit={canEdit}
                                editBlocked={editBlocked}
                                onView={() =>
                                    setViewing({
                                        slot: index,
                                        current: slotConfig,
                                        pending: pendingConfig
                                    })
                                }
                                onEdit={() => setEditingSlot(index)}
                                onDelete={() => confirmDelete(index)}
                                onCancel={() =>
                                    confirmCancel(() =>
                                        cancelDeviceGeoAlert({ slot: index })
                                    )
                                }
                            />
                        );
                    })}
                </tbody>
            </table>
            <GeofenceAlertModal
                deviceId={device.id}
                slot={editingSlot ?? 0}
                current={
                    editingSlot !== null
                        ? device.config?.geoAlert?.[String(editingSlot)]
                        : undefined
                }
                isOpen={editingSlot !== null}
                onClose={() => setEditingSlot(null)}
            />
            <GeofenceViewModal
                slot={viewing?.slot ?? 0}
                current={viewing?.current}
                pendingConfig={viewing?.pending}
                isOpen={viewing !== null}
                onClose={() => setViewing(null)}
            />
        </>
    );
}

function HeaderText(props: { text: string }): ReactElement {
    return (
        <UIText variant="small" type="default2">
            {props.text}
        </UIText>
    );
}

/** Display strings for one geofence slot's columns (current or pending side). */
type GeoCells = {
    enabled: boolean;
    status: string;
    shape: string;
    direction: string;
    size: string;
};

/**
 * One geofence slot row. When a change is pending, each column shows the current
 * value transitioning to the pending one ("old → new"), mirroring the Motion
 * Alerts design language, and the slot's edit/delete actions are replaced by the
 * clickable "Pending" badge.
 */
function GeofenceSlotRow(props: {
    index: number;
    slot: DeviceConfigGeoAlert | undefined;
    pendingCommand: DeviceCommand | undefined;
    pendingConfig: DeviceConfigGeoAlert | undefined;
    canEdit: boolean;
    editBlocked: boolean;
    onView: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onCancel: () => void;
}): ReactElement {
    const {
        index,
        slot,
        pendingCommand,
        pendingConfig,
        canEdit,
        editBlocked,
        onView,
        onEdit,
        onDelete,
        onCancel
    } = props;
    const { t } = useDevicesTranslation();

    const current = currentCells(slot, t);
    const pending = pendingCommand
        ? pendingCells(pendingCommand.data, t)
        : undefined;
    const enabled = current.enabled;

    // Show the view button when there's a current shape to inspect or a viewable
    // pending shape; the modal itself toggles between the two.
    const pendingViewable =
        !!pendingConfig?.enabled && pendingConfig.shape !== null;
    const canView = enabled || pendingViewable;

    // The on/off arrow lives on the Status column. Detail columns only diff when
    // both sides are enabled; on an enable/disable change they show a single side
    // (the config being turned off, or the new config) rather than "Circle → —".
    const bothEnabled = !!pending && current.enabled && pending.enabled;
    const details =
        pending && !bothEnabled && pending.enabled ? pending : current;

    return (
        <tr>
            <td>
                <UIText>{index + 1}</UIText>
            </td>
            <td>
                <StatusCell current={current} pending={pending} />
            </td>
            <td>
                <DiffCell
                    current={details.shape}
                    pending={bothEnabled ? pending?.shape : undefined}
                />
            </td>
            <td>
                <DiffCell
                    current={details.direction}
                    pending={bothEnabled ? pending?.direction : undefined}
                />
            </td>
            <td>
                <DiffCell
                    current={details.size}
                    pending={bothEnabled ? pending?.size : undefined}
                />
            </td>
            <td>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 4
                    }}
                >
                    {canView && (
                        <UITooltip text={t('shared.labels.view')}>
                            <UIButton
                                leftIcon="eye"
                                variant="textDefault"
                                type="button"
                                customPadding={6}
                                onClick={onView}
                            />
                        </UITooltip>
                    )}
                    {pendingCommand ? (
                        <PendingBadge
                            label={t('shared.labels.pending')}
                            tooltip={
                                canEdit
                                    ? t('shared.devices.cancel_pending_confirm')
                                    : undefined
                            }
                            onCancel={canEdit ? onCancel : undefined}
                        />
                    ) : (
                        canEdit && (
                            <>
                                <UITooltip text={t('shared.labels.edit')}>
                                    <UIButton
                                        leftIcon="edit-2"
                                        variant="textDefault"
                                        type="button"
                                        customPadding={6}
                                        disabled={editBlocked}
                                        onClick={onEdit}
                                    />
                                </UITooltip>
                                {enabled && (
                                    <UITooltip text={t('shared.labels.delete')}>
                                        <UIButton
                                            leftIcon="trash-2"
                                            variant="textDanger"
                                            type="button"
                                            customPadding={6}
                                            onClick={onDelete}
                                        />
                                    </UITooltip>
                                )}
                            </>
                        )
                    )}
                </div>
            </td>
        </tr>
    );
}

/** Column display strings for the slot's current reported state. */
function currentCells(
    slot: DeviceConfigGeoAlert | undefined,
    t: ReturnType<typeof useDevicesTranslation>['t']
): GeoCells {
    const enabled = slot?.enabled ?? false;
    if (!enabled || !slot) {
        return {
            enabled,
            status: enabled
                ? t('shared.labels.enabled')
                : t('shared.labels.disabled'),
            shape: EMPTY,
            direction: EMPTY,
            size: EMPTY
        };
    }
    return {
        enabled,
        status: t('shared.labels.enabled'),
        shape:
            slot.shape === 'circle'
                ? t('shared.devices.shape_circle')
                : slot.shape === 'polygon'
                  ? t('shared.devices.shape_polygon')
                  : EMPTY,
        direction:
            slot.direction === 'out'
                ? t('shared.devices.direction_out')
                : t('shared.devices.direction_in'),
        size: buildSize(slot, t)
    };
}

/** Column display strings for a pending set_geo_alert command's target state. */
function pendingCells(
    data: DeviceCommand['data'],
    t: ReturnType<typeof useDevicesTranslation>['t']
): GeoCells {
    const enabled = !!data.geoAlertEnabled;
    if (!enabled) {
        return {
            enabled,
            status: t('shared.labels.disabled'),
            shape: EMPTY,
            direction: EMPTY,
            size: EMPTY
        };
    }
    const shape =
        data.geoAlertShape === 'circle'
            ? t('shared.devices.shape_circle')
            : data.geoAlertShape === 'polygon'
              ? t('shared.devices.shape_polygon')
              : EMPTY;
    const direction =
        data.geoAlertDirection === 'out'
            ? t('shared.devices.direction_out')
            : t('shared.devices.direction_in');
    const size =
        data.geoAlertShape === 'circle' && data.geoAlertRadius != null
            ? t('shared.devices.radius_value', { value: data.geoAlertRadius })
            : data.geoAlertShape === 'polygon'
              ? t('shared.devices.points_value', {
                    count: data.geoAlertPoints?.length ?? 0
                })
              : EMPTY;
    return {
        enabled,
        status: t('shared.labels.enabled'),
        shape,
        direction,
        size
    };
}

function buildSize(
    slot: DeviceConfigGeoAlert,
    t: ReturnType<typeof useDevicesTranslation>['t']
): string {
    if (slot.shape === 'circle' && slot.radius !== null) {
        return t('shared.devices.radius_value', { value: slot.radius });
    }
    if (slot.shape === 'polygon') {
        return t('shared.devices.points_value', {
            count: slot.points?.length ?? 0
        });
    }
    return EMPTY;
}

/** Shape a pending set_geo_alert command's data into a viewable geofence config. */
function pendingToGeoAlert(data: DeviceCommand['data']): DeviceConfigGeoAlert {
    return {
        enabled: !!data.geoAlertEnabled,
        direction: data.geoAlertDirection === 'out' ? 'out' : 'in',
        shape:
            data.geoAlertShape === 'circle' || data.geoAlertShape === 'polygon'
                ? data.geoAlertShape
                : null,
        radius: data.geoAlertRadius ?? null,
        points: data.geoAlertPoints ?? null,
        updatedAt: ''
    };
}
